import { STANDARD_AUDIENCE, useUser } from "@/providers/user";
import { MetadataService } from "@/services/metadata";
import { OptionsResponse } from "@/types/dynamic-detail";
import { PermissionAudience, UserGroups, UserProfile } from "@/types/user";
import {
    BanknoteArrowDown,
    BetweenHorizonalStart,
    BookOpen,
    Building2,
    ChartArea,
    ChartNoAxesCombined,
    GraduationCap,
    LibraryBig,
    Mail,
    Mailbox,
    Megaphone,
    MessageSquare,
    MessageSquareDiff,
    Palette,
    PhoneForwarded,
    ReceiptEuro,
    Recycle,
    ShieldUser,
    User,
    Zap
} from "lucide-react";
import { ReactElement, useEffect, useState } from "react";

/**
 * Navigation of the standard layout, the same for every account type.
 *
 * Every entry and every piece of the layout declares who may see it, like the `ObjectConfig`
 * elements server-side:
 * - `audiences` (`PermissionAudience`), default standard only: an entry is only shown to partner
 *   accounts when it lists `partner`, and only lists it when what it opens works for them (the
 *   partner gate refuses everything else server-side);
 * - `groups` / `profiles`, as before (an entry that declares neither is open to its audiences);
 * - object entries (`/<object_url>`) are also shown only when the object's metadata opens its list
 *   to the user (`is_access_allowed`, `permissions.list`), i.e. their permission rows decide.
 *
 * The audience comes from the server (`/users/me/` → `permission_audience`).
 */
export type MenuEntry = {
    title: string;
    href: string;
    icon?: ReactElement;
    groups?: UserGroups[];
    profiles?: UserProfile[];
    audiences?: readonly PermissionAudience[];
};

/** Audiences of the pieces of the standard layout that are not menu entries. */
export const LAYOUT_AUDIENCES = {
    /** Own notifications (bell, `/notifications`): self-scoped server-side. */
    notifications: ["standard", "partner"],
    /** Tracking of the user's own activity (`/users/activities/track/`). */
    activityTracking: ["standard", "partner"],
    /** Favorites and recent records (header dropdown, object entries' popover). */
    quickAccess: STANDARD_AUDIENCE,
    /** Saved list views: the view selector's views and the list view controls menu (save, save as, rename, delete).
     * A partner's are their own, always private (`walter_crm.partner_gate.PARTNER_LIST_VIEW_ACTIONS`). */
    listViews: ["standard", "partner"],
    /** Sharing a saved view with users and roles, and its quick charts. */
    listViewSharing: STANDARD_AUDIENCE,
    /** Reminders of the user's calendar events (polled on every page). */
    eventAlerts: STANDARD_AUDIENCE,
    globalActions: STANDARD_AUDIENCE,
    perfTracking: STANDARD_AUDIENCE,
    /** Telephony, SMS, Walter IA. */
    utilityBar: STANDARD_AUDIENCE,
    /** Avatar menu: own user record and settings. */
    profileLinks: STANDARD_AUDIENCE,
    /** The legacy menu (opt-in from the settings). */
    legacyMenu: STANDARD_AUDIENCE
} satisfies Record<string, readonly PermissionAudience[]>;

/**
 * Whether the saved list views are fetched for `audience`. Unknown audience (the user is loading): yes, so that a
 * standard user's pinned or linked view is not replaced by the default one before the audience is known.
 */
export function offersSavedViews(audience: PermissionAudience | null): boolean {
    return audience === null || (LAYOUT_AUDIENCES.listViews as readonly PermissionAudience[]).includes(audience);
}

export const HOME_ENTRY: MenuEntry = { title: "Home", href: "/" };
export const CALENDAR_ENTRY: MenuEntry = { title: "Calendar", href: "/calendar" };
/** Internal registration screen (shown to profiles other than `default`). */
export const SALES_FORMS_ENTRY: MenuEntry = { title: "Sales forms", href: "/inscriptions/ventes" };
/** Legacy screen of external providers' accounts. */
export const EXTERNAL_REGISTRATIONS_ENTRY: MenuEntry = { title: "Registrations", href: "/inscriptions/externes" };
/** Medical reps' contacts ("Contacts", or "Medical Reps" for registration admins). */
export const MEDREP_ENTRY: MenuEntry = { title: "Medical Reps", href: "/dm/", groups: ["app_medrep_admin", "team_medrep"] };

export const KEY_OBJECTS: MenuEntry[] = [
    { title: "Candidates", href: "/candidates" },
    { title: "Opportunities", href: "/opportunities" },
    { title: "Cases", href: "/cases" },
    { title: "Leads", href: "/leads" },
    { title: "Tasks", href: "/tasks" },
    { title: "Calls", href: "/calls" },
    { title: "Accounts", href: "/accounts" },
    // Partner accounts' registrations (generic record pages). Internal users reach theirs from
    // "Sales forms" until that screen moves to the generic engine.
    { title: "Registrations", href: "/registrations", audiences: ["partner"] },
    // Partner supervisors and managers: their team, and the account requests sent to Sales Ops.
    // A sales rep has no row on either object, so the object permissions hide both.
    { title: "My team", href: "/team-members", audiences: ["partner"] },
    // Also internal: Sales Ops handle the queue (shown to the profiles that have a row on it).
    { title: "Account requests", href: "/partner-account-requests", audiences: ["standard", "partner"] }
];

const iconClass = "mb-0.5 mr-1.5 inline-block";

export const OTHER_OBJECTS: MenuEntry[] = [
    // Sorted alphabetically.
    {
        // Readable in the CRM by system admins only (emails.0003); same gate as Email Templates.
        title: "Brands",
        href: "/brands",
        icon: <Palette size={16} className={iconClass} strokeWidth={2} />,
        profiles: ["system_admin"]
    },
    {
        // Readable by every profile (list views filter on it); listed for the admins who maintain it.
        title: "Business Groups",
        href: "/business-groups",
        icon: <Building2 size={16} className={iconClass} strokeWidth={2} />,
        profiles: ["system_admin"]
    },
    { title: "Certification Registrations", href: "/certification-registrations", icon: <GraduationCap size={16} className={iconClass} strokeWidth={2} /> },
    { title: "Contacts", href: "/contacts", icon: <User size={16} className={iconClass} strokeWidth={2} /> },
    {
        // The dialer engine. Workflows and cards are reached from a campaign,
        // so this single entry opens the whole `dialer` surface.
        title: "Dialer campaigns",
        href: "/dialer-campaigns",
        icon: <PhoneForwarded size={16} className={iconClass} strokeWidth={2} />
    },
    {
        // Readable and writable through MCP by system admins only, for now.
        title: "Email Templates",
        href: "/email-templates",
        icon: <Mail size={16} className={iconClass} strokeWidth={2} />,
        profiles: ["system_admin"]
    },
    { title: "Flash Injections", href: "/flash-injections", icon: <Zap size={16} className={iconClass} strokeWidth={2} /> },
    { title: "Injection Markers", href: "/injection-markers", icon: <BetweenHorizonalStart size={16} className={iconClass} strokeWidth={2} /> },
    { title: "Installment Schedules", href: "/installment-schedules", icon: <BanknoteArrowDown size={16} className={iconClass} strokeWidth={2} /> },
    { title: "Invoice Groups", href: "/invoice-groups", icon: <LibraryBig size={16} className={iconClass} strokeWidth={2} /> },
    { title: "Invoices", href: "/invoices", icon: <ReceiptEuro size={16} className={iconClass} strokeWidth={2} /> },
    { title: "Mailing Campaigns", href: "/mailing-campaigns", icon: <Mailbox size={16} className={iconClass} strokeWidth={2} /> },
    { title: "Mailing Leads", href: "/mailing-leads", icon: <Mailbox size={16} className={iconClass} strokeWidth={2} /> },
    { title: "Marketing Campaign Insights", href: "/marketing-campaign-insights", icon: <ChartArea size={16} className={iconClass} strokeWidth={2} /> },
    { title: "Marketing Campaigns", href: "/marketing-campaigns", icon: <Megaphone size={16} className={iconClass} strokeWidth={2} /> },
    { title: "Productivity Reports", href: "/user-productivity-reports", icon: <ChartNoAxesCombined size={16} className={iconClass} strokeWidth={2} /> },
    { title: "Products", href: "/products", icon: <BookOpen size={16} className={iconClass} strokeWidth={2} /> },
    { title: "Recycling Markers", href: "/recycling-markers", icon: <Recycle size={16} className={iconClass} strokeWidth={2} /> },
    { title: "Recycling Waves", href: "/recycling-waves", icon: <Recycle size={16} className={iconClass} strokeWidth={2} /> },
    { title: "Sales Activity Reviews", href: "/sales-activity-reviews", icon: <MessageSquareDiff size={16} className={iconClass} strokeWidth={2} /> },
    {
        // Readable in the CRM and writable through MCP by system admins only, like Email Templates.
        title: "SMS Templates",
        href: "/sms-templates",
        icon: <MessageSquare size={16} className={iconClass} strokeWidth={2} />,
        profiles: ["system_admin"]
    },
    { title: "Users", href: "/users", icon: <ShieldUser size={16} className={iconClass} strokeWidth={2} /> }
];

/** `canSee(entry)`: audience, then groups / profiles (see `MenuEntry`). Object permissions apart. */
export function useMenuEntryAccess() {
    const { has_any_group, has_any_profile, is_visible_to_audience } = useUser();
    return (entry: MenuEntry) => {
        if (!is_visible_to_audience(entry.audiences)) return false;
        const groups = entry.groups ?? [];
        const profiles = entry.profiles ?? [];
        if (groups.length === 0 && profiles.length === 0) return true;
        return (groups.length > 0 && has_any_group(groups)) || (profiles.length > 0 && has_any_profile(profiles));
    };
}

async function loadObjectPermissions(entries: MenuEntry[]): Promise<Record<string, boolean>> {
    const permissions: Record<string, boolean> = {};
    await Promise.all(
        entries.map(async entry => {
            try {
                const objectName = entry.href.replace(/\/$/, "").replace("/", "");
                const metadata = (await MetadataService.get(`/crm/objects/${objectName}/`)) as OptionsResponse | null;
                permissions[entry.href] = !!metadata && metadata.is_access_allowed === true && metadata.permissions?.list !== "none";
            } catch (e) {
                console.error(`Error loading permissions for ${entry.href}:`, e);
                permissions[entry.href] = false;
            }
        })
    );
    return permissions;
}

/**
 * Object entries the user may open: the key objects first, then the others in the background.
 *
 * Only entries of the user's audience are asked for (`canSee`), so an account never fetches the
 * metadata of an object its audience has no entry for. Waits for the user to be known.
 */
export function useObjectMenuPermissions({ enabled = true }: { enabled?: boolean } = {}) {
    const { audience } = useUser();
    const canSee = useMenuEntryAccess();
    const [objectPermissions, setObjectPermissions] = useState<Record<string, boolean>>({});
    const [isLoadingPermissions, setIsLoadingPermissions] = useState(true);
    const [isLoadingOthers, setIsLoadingOthers] = useState(true);

    useEffect(() => {
        if (!enabled || audience === null) return;
        let cancelled = false;
        loadObjectPermissions(KEY_OBJECTS.filter(canSee))
            .then(permissions => {
                if (cancelled) return;
                setObjectPermissions(prev => ({ ...prev, ...permissions }));
                setIsLoadingPermissions(false);
                return loadObjectPermissions(OTHER_OBJECTS.filter(canSee));
            })
            .then(permissions => {
                if (cancelled || !permissions) return;
                setObjectPermissions(prev => ({ ...prev, ...permissions }));
                setIsLoadingOthers(false);
            });
        return () => {
            cancelled = true;
        };
        // `canSee` only changes with the user's audience, groups and profile.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enabled, audience]);

    const isObjectEntryVisible = (entry: MenuEntry) => canSee(entry) && objectPermissions[entry.href] === true;
    return { isObjectEntryVisible, isLoadingPermissions, isLoadingOthers };
}

/**
 * Where a user who may not use the home dashboard lands: their first object entry, in menu order.
 * `undefined` while loading, `null` when nothing is open to them.
 */
export function useLandingHref(): string | null | undefined {
    const { audience } = useUser();
    const canSee = useMenuEntryAccess();
    const seesHome = canSee(HOME_ENTRY);
    const { isObjectEntryVisible, isLoadingPermissions, isLoadingOthers } = useObjectMenuPermissions({ enabled: !seesHome });
    if (audience === null) return undefined;
    if (seesHome) return HOME_ENTRY.href;
    const firstKey = KEY_OBJECTS.find(isObjectEntryVisible);
    if (firstKey) return firstKey.href;
    if (isLoadingPermissions || isLoadingOthers) return undefined;
    const firstOther = [...OTHER_OBJECTS].sort((a, b) => a.title.localeCompare(b.title)).find(isObjectEntryVisible);
    return firstOther ? firstOther.href : null;
}

/**
 * Whether the user cached by `UserProvider` (`localStorage.current_user`) may see the home dashboard,
 * for route loaders, which run outside React. A user not cached yet counts as standard.
 */
export function cachedUserSeesHome(): boolean {
    try {
        const user = JSON.parse(localStorage.getItem("current_user") || "null") as { permission_audience?: PermissionAudience } | null;
        const audience: PermissionAudience = user?.permission_audience ?? "standard";
        return (HOME_ENTRY.audiences ?? STANDARD_AUDIENCE).includes(audience);
    } catch {
        return true;
    }
}
